import * as THREE from 'three';
import { GPUComputationRenderer } from 'three/examples/jsm/misc/GPUComputationRenderer';
import GUI from 'lil-gui';
import FloatingCards from '../FloatingCards.js';
import velocityFragmentShader from '../shaders/velocityFragmentShader.glsl';
import positionFragmentShader from '../shaders/positionFragmentShader.glsl';
import debounceFragmentShader from '../shaders/debounceFragmentShader.glsl';
import * as CARDS from '../constants/cardsConstants.js';

export default class GPUPhysicsSimulator {
  constructor() {
    // Core setup
    this.floatingCards = new FloatingCards();
    this.cards = this.floatingCards.cards;
    this.camera = this.floatingCards.camera;
    this.renderer = this.floatingCards.renderer.instance;

    // Debug flag
    this.debug = false;

    // Compute texture dimensions
    this.PARTICLES = this.cards.cards.length;
    this.WIDTH = Math.ceil(Math.sqrt(this.PARTICLES));
    this.PARTICLES = this.WIDTH * this.WIDTH;

    // Physics parameters
    this.params = {
      // Force & Interaction Parameters
      forceStrength: CARDS.FORCE_STRENGTH,
      influenceFalloff: CARDS.INFLUENCE_FALLOFF,
      maxDistance: CARDS.MAX_DISTANCE,

      // Velocity & Movement Parameters
      damping: CARDS.DAMPING,
      minVelocity: CARDS.MIN_VELOCITY,
      maxVelocity: CARDS.MAX_VELOCITY,

      // Return & Stabilization
      returnSpeed: CARDS.RETURN_SPEED,
      returnVelocityThreshold: CARDS.RETURN_VELOCITY_THRESHOLD,
      debounceTime: CARDS.DEBOUNCE_TIME,

      // Input Device Sensitivity
      movementScale: CARDS.MOVEMENT_SCALE,
      trackpadScale: CARDS.TRACKPAD_SCALE,
      maxDelta: CARDS.MAX_DELTA,

      // Shockwave Effect (Click/Tap)
      shockwaveStrength: CARDS.SHOCKWAVE_STRENGTH,
      shockwaveRadius: CARDS.SHOCKWAVE_RADIUS,
      shockwaveFalloff: CARDS.SHOCKWAVE_FALLOFF,
      shockwaveDecay: CARDS.SHOCKWAVE_DECAY
    };

    this._initializeOriginalPositions();
    this.initGPUCompute();
    if (this.debug) {
      this.initGUI();
    }
    this.setupMouseInteraction();

    this.shockwave = {
      active: false,
      position: new THREE.Vector2(),
      time: 0
    };
  }

  _initializeOriginalPositions() {
    this.originalPositions = new Float32Array(this.WIDTH * this.WIDTH * 4);
    const cardsLength = this.cards.cards.length;

    for (let i = 0; i < this.PARTICLES; i++) {
      const i4 = i * 4;
      if (i < cardsLength) {
        const [key, cardData] = this.cards.cards[i];
        this.originalPositions[i4] = cardData.positionXScene;
        this.originalPositions[i4 + 1] = cardData.positionYScene;
        this.originalPositions[i4 + 2] = 0;
        this.originalPositions[i4 + 3] = 1.0;
      } else {
        this.originalPositions.fill(9999, i4, i4 + 4);
      }
    }
  }

  _createDataTexture(data) {
    const texture = new THREE.DataTexture(
      data,
      this.WIDTH,
      this.WIDTH,
      THREE.RGBAFormat,
      THREE.FloatType
    );
    texture.needsUpdate = true;
    texture.minFilter = THREE.NearestFilter;
    texture.magFilter = THREE.NearestFilter;
    texture.generateMipmaps = false;
    return texture;
  }

  initGPUCompute() {
    this.gpuCompute = new GPUComputationRenderer(
      this.WIDTH,
      this.WIDTH,
      this.renderer
    );

    if (!this.renderer.capabilities.isWebGL2 &&
      !this.renderer.extensions.get('OES_texture_float')) {
      console.error('Floating point textures not supported');
      return;
    }

    // Create initial textures
    const positionTexture = this._createDataTexture(this.originalPositions.slice());
    const velocityTexture = this._createDataTexture(new Float32Array(this.WIDTH * this.WIDTH * 4));
    const debounceTexture = this._createDataTexture(new Float32Array(this.WIDTH * this.WIDTH * 4));

    // Initialize computation variables
    this.positionVariable = this.gpuCompute.addVariable(
      'positionTexture',
      positionFragmentShader,
      positionTexture
    );

    this.velocityVariable = this.gpuCompute.addVariable(
      'velocityTexture',
      velocityFragmentShader,
      velocityTexture
    );

    this.debounceVariable = this.gpuCompute.addVariable(
      'debounceTexture',
      debounceFragmentShader,
      debounceTexture
    );

    // Set variable dependencies
    this.gpuCompute.setVariableDependencies(this.velocityVariable,
      [this.positionVariable, this.velocityVariable, this.debounceVariable]);
    this.gpuCompute.setVariableDependencies(this.positionVariable,
      [this.positionVariable, this.velocityVariable]);
    this.gpuCompute.setVariableDependencies(this.debounceVariable,
      [this.positionVariable, this.debounceVariable]);

    // Initialize uniforms
    this._setupUniforms();

    const error = this.gpuCompute.init();
    if (error !== null) {
      console.error('GPUCompute initialization failed:', error);
    }
  }

  _setupUniforms() {
    // Velocity shader uniforms
    this.velocityUniforms = this.velocityVariable.material.uniforms;
    Object.assign(this.velocityUniforms, {
      mousePosition: { value: new THREE.Vector2() },
      mouseDelta: { value: new THREE.Vector2() },
      deltaTime: { value: 0.0 },
      forceStrength: { value: this.params.forceStrength },
      influenceFalloff: { value: this.params.influenceFalloff },
      damping: { value: this.params.damping },
      maxDistance: { value: this.params.maxDistance },
      minVelocity: { value: this.params.minVelocity },
      maxVelocity: { value: this.params.maxVelocity },
      debounceTime: { value: this.params.debounceTime },
      shockwaveActive: { value: false },
      shockwavePosition: { value: new THREE.Vector2() },
      shockwaveTime: { value: 0.0 },
      shockwaveStrength: { value: this.params.shockwaveStrength },
      shockwaveRadius: { value: this.params.shockwaveRadius },
      shockwaveFalloff: { value: this.params.shockwaveFalloff },
      shockwaveDecay: { value: this.params.shockwaveDecay }
    });

    // Position shader uniforms
    this.positionUniforms = this.positionVariable.material.uniforms;
    Object.assign(this.positionUniforms, {
      deltaTime: { value: 0.0 },
      originalPositionTexture: { value: this._createDataTexture(this.originalPositions) },
      returnSpeed: { value: this.params.returnSpeed },
      returnVelocityThreshold: { value: this.params.returnVelocityThreshold }
    });

    // Debounce shader uniforms
    this.debounceUniforms = this.debounceVariable.material.uniforms;
    Object.assign(this.debounceUniforms, {
      deltaTime: { value: 0.0 },
      debounceTime: { value: this.params.debounceTime },
      mousePosition: { value: new THREE.Vector2() },
      maxDistance: { value: this.params.maxDistance }
    });
  }

  setupMouseInteraction() {
    this.mouseNDC = new THREE.Vector2();
    this.raycaster = new THREE.Raycaster();
    this.mousePosition = new THREE.Vector2();
    this.mouseDelta = new THREE.Vector2();
    this.prevMousePosition = new THREE.Vector2();
    this.intersectionPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    this.intersectionPoint = new THREE.Vector3();
    this.lastUpdateTime = performance.now();

    this.movementScale = 1.0;

    window.addEventListener('mousemove', this.onMouseMove.bind(this));
    window.addEventListener('mousedown', this.onMouseClick.bind(this));
    window.addEventListener('mousemove', this.detectInputDevice.bind(this), { once: true });
  }

  detectInputDevice(event) {
    // Trackpads typically have smaller acceleration
    const acceleration = Math.abs(event.movementX) + Math.abs(event.movementY);
    if (acceleration < 5) {
      this.movementScale = this.params.trackpadScale;
      console.log('Trackpad detected, adjusting sensitivity');
    } else {
      this.movementScale = this.params.movementScale;
    }
  }

  onMouseMove(event) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouseNDC.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouseNDC.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouseNDC, this.camera.instance);
    const intersects = this.raycaster.ray.intersectPlane(
      this.intersectionPlane,
      this.intersectionPoint
    );

    if (intersects) {
      this.prevMousePosition.copy(this.mousePosition);
      this.mousePosition.set(this.intersectionPoint.x, this.intersectionPoint.y);

      const currentTime = performance.now();
      const deltaTime = (currentTime - this.lastUpdateTime) / 16.667;
      this.lastUpdateTime = currentTime;

      const rawDelta = new THREE.Vector2().subVectors(this.mousePosition, this.prevMousePosition);
      this.mouseDelta.copy(rawDelta.multiplyScalar(this.movementScale / Math.max(0.1, deltaTime)));

      if (this.mouseDelta.length() > this.params.maxDelta) {
        this.mouseDelta.normalize().multiplyScalar(this.params.maxDelta);
      }
    }
  }

  onMouseClick(event) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouseNDC.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouseNDC.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouseNDC, this.camera.instance);
    const intersects = this.raycaster.ray.intersectPlane(
      this.intersectionPlane,
      this.intersectionPoint
    );

    if (intersects) {
      this.shockwave.active = true;
      this.shockwave.position.set(this.intersectionPoint.x, this.intersectionPoint.y);
      this.shockwave.time = 0;
    }
  }

  initGUI() {
    // Only initialize GUI if debug is enabled
    if (!this.debug) return;

    const gui = new GUI();
    const physics = gui.addFolder('Physics Parameters');

    // Force and Interaction
    const force = physics.addFolder('Force & Interaction');
    force.add(this.params, 'forceStrength', ...CARDS.PARAM_RANGES.forceStrength)
      .onChange(value => {
        this.velocityUniforms.forceStrength.value = value;
      });
    force.add(this.params, 'influenceFalloff', ...CARDS.PARAM_RANGES.influenceFalloff)
      .onChange(value => {
        this.velocityUniforms.influenceFalloff.value = value;
      });
    force.add(this.params, 'maxDistance', ...CARDS.PARAM_RANGES.maxDistance)
      .onChange(value => {
        this.velocityUniforms.maxDistance.value = value;
        this.debounceUniforms.maxDistance.value = value;
      });

    // Velocity Controls
    const velocity = physics.addFolder('Velocity Controls');
    velocity.add(this.params, 'damping', ...CARDS.PARAM_RANGES.damping)
      .onChange(value => {
        this.velocityUniforms.damping.value = value;
      });
    velocity.add(this.params, 'minVelocity', ...CARDS.PARAM_RANGES.minVelocity)
      .onChange(value => {
        this.velocityUniforms.minVelocity.value = value;
      });
    velocity.add(this.params, 'maxVelocity', ...CARDS.PARAM_RANGES.maxVelocity)
      .onChange(value => {
        this.velocityUniforms.maxVelocity.value = value;
      });

    // Return and Stabilization
    const stability = physics.addFolder('Return & Stabilization');
    stability.add(this.params, 'returnSpeed', ...CARDS.PARAM_RANGES.returnSpeed)
      .onChange(value => {
        this.positionUniforms.returnSpeed.value = value;
      });
    stability.add(this.params, 'returnVelocityThreshold', ...CARDS.PARAM_RANGES.returnVelocityThreshold)
      .name('Return Start Speed')
      .onChange(value => {
        this.positionUniforms.returnVelocityThreshold.value = value;
      });
    stability.add(this.params, 'debounceTime', ...CARDS.PARAM_RANGES.debounceTime)
      .onChange(value => {
        this.velocityUniforms.debounceTime.value = value;
        this.debounceUniforms.debounceTime.value = value;
      });

    // Input Device Controls
    const input = physics.addFolder('Input Device');
    input.add(this.params, 'movementScale', ...CARDS.PARAM_RANGES.movementScale)
      .name('Mouse Scale')
      .onChange(value => {
        if (this.movementScale === this.params.movementScale) {
          this.movementScale = value;
        }
      });
    input.add(this.params, 'trackpadScale', ...CARDS.PARAM_RANGES.trackpadScale)
      .name('Trackpad Scale')
      .onChange(value => {
        if (this.movementScale === this.params.trackpadScale) {
          this.movementScale = value;
        }
      });
    input.add(this.params, 'maxDelta', ...CARDS.PARAM_RANGES.maxDelta)
      .name('Max Movement');

    // Shockwave Controls
    const shockwave = physics.addFolder('Shockwave Effect');
    shockwave.add(this.params, 'shockwaveStrength', ...CARDS.PARAM_RANGES.shockwaveStrength)
      .name('Strength')
      .onChange(value => {
        this.velocityUniforms.shockwaveStrength.value = value;
      });
    shockwave.add(this.params, 'shockwaveRadius', ...CARDS.PARAM_RANGES.shockwaveRadius)
      .name('Radius')
      .onChange(value => {
        this.velocityUniforms.shockwaveRadius.value = value;
      });
    shockwave.add(this.params, 'shockwaveFalloff', ...CARDS.PARAM_RANGES.shockwaveFalloff)
      .name('Falloff')
      .onChange(value => {
        this.velocityUniforms.shockwaveFalloff.value = value;
      });
    shockwave.add(this.params, 'shockwaveDecay', ...CARDS.PARAM_RANGES.shockwaveDecay)
      .name('Decay')
      .onChange(value => {
        this.velocityUniforms.shockwaveDecay.value = value;
      });

    // Open folders
    physics.open();
    force.open();
    velocity.open();
    stability.open();
    input.open();
    shockwave.open();
  }

  update(deltaTime) {
    if (this.shockwave.active) {
      this.shockwave.time += deltaTime;
      if (this.shockwave.time >= 1.0) {
        this.shockwave.active = false;
      }
    }

    this.velocityUniforms.deltaTime.value = deltaTime;
    this.velocityUniforms.mousePosition.value.copy(this.mousePosition);
    this.velocityUniforms.mouseDelta.value.copy(this.mouseDelta);
    this.velocityUniforms.shockwaveActive.value = this.shockwave.active;
    this.velocityUniforms.shockwavePosition.value.copy(this.shockwave.position);
    this.velocityUniforms.shockwaveTime.value = this.shockwave.time;
    this.positionUniforms.deltaTime.value = deltaTime;
    this.debounceUniforms.deltaTime.value = deltaTime;
    this.debounceUniforms.mousePosition.value.copy(this.mousePosition);

    // Perform physics computation
    this.gpuCompute.compute();

    // Reset mouse delta after computation
    this.mouseDelta.set(0, 0);
  }

  getCurrentPositionTexture() {
    return this.gpuCompute.getCurrentRenderTarget(this.positionVariable).texture;
  }

  getCurrentVelocityTexture() {
    return this.gpuCompute.getCurrentRenderTarget(this.velocityVariable).texture;
  }
}
